import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import appConfig from "../../config";
import DataDisplayFactory from "./data-display/data-display-factory";
import { voiceModeChange } from "../../redux/app/app-actions";
import VoiceHeader from "./voice-header";
import AudioPlayer from "./audio-streamer";
import VoiceIndicator from "./voice-Indicator";
import AudioControlTool from "./audio-control-tool";
import { AnalysingIndicator, AnalysingToast } from "./analysing-Indicator";
import ReadingIndicator from "./reading-Indicator";
import { WavRecorder, WavStreamPlayer } from '../../helpers/wavtools';

const AUDIO_SAMPLE_RATE = 24000;

const init_state = "initializing";
const listening_state = "listening";
const thinking_state = "thinking";
const reading_answer_state = "reading_answer";
const pause_state = "pause";
const failed_state = "failed";

function VoiceModePlugin({
  accessKey,
  appId,
  sessionId,
  voiceModeChangeHandle,
  onInvokeAction,
  settingsState,
  chatBotSetting,
  toggleVoiceMode, // Add toggleVoiceMode prop
}) {
  const webSocket = useRef(null);
  const wavRecorderRef = useRef(new WavRecorder({ sampleRate: AUDIO_SAMPLE_RATE }));

  const wavStreamPlayerRef = useRef(new WavStreamPlayer({ sampleRate: AUDIO_SAMPLE_RATE }));

  const [initialBotReplyReceived, setInitialBotReplyReceived] = useState(false);
  const [textReplayMessage, setTextReplayMessage] = useState(null);
  const [voiceReplayMessage, setVoiceReplayMessage] = useState(null);
  const [voiceReplayBytes, setVoiceReplayBytes] = useState(null);

  const [state, setState] = useState(init_state);

  const [userVoiceIndicatorSize, setUserVoiceIndicator] = useState(10); // Default size
  const [userVoiceIndicatorBgColor, setUserVoiceIndicatorBgColor] =
    useState("gray"); // Default color

  const [testUserSpeaking, setTestUserSpeaking] = useState(false);

  function initConnection() {
    webSocket.current = new WebSocket(
      `${appConfig.VOICE_STREAM_API}?access_key=${accessKey}&app_id=${appId}&session_id=${sessionId}`
    );
    webSocket.current.binaryType = "arraybuffer"

    webSocket.current.onmessage = (event) => {
      if (typeof event.data === "string") {
        const message = JSON.parse(event.data);
        // TODO old actions. remove later
        // if (message.loading) {
        //   setVoiceReplayMessage(null);
        //   setTextReplayMessage(null);
        //   setState(thinking_state);
        // } else {
        //   setInitialBotReplyReceived(true);
        //   setState(reading_answer_state);
        //   if (message.voiceMessage) {
        //     setVoiceReplayMessage(message.voiceMessage);
        //   }
        //   if (message.message) {
        //     setTextReplayMessage(message.message);
        //   }
        // }

        const { action, content } = message

        if (action === 'INTERRUPT') {
          wavStreamPlayerRef.current.stop()
          setTextReplayMessage(null)
          setState(thinking_state)
        }

      } else {
        // receives bytes
        const int16ArrayMessage = new Int16Array(event.data)
        wavStreamPlayerRef.current.add16BitPCM(int16ArrayMessage)
        setState(listening_state)
      }
    };

    webSocket.current.onopen = () => {
      initSetup();
    };

    webSocket.current.onclose = () => {
      console.log("ws onclose");
    };
  }

  async function initSetup() {
    await setUpRecorder();

    // TODO should be come from config
    // await onInvokeAction("hi", {});

    const wavStreamPlayer = wavStreamPlayerRef.current;

    // Connect to audio output
    await wavStreamPlayer.connect();
  }

  async function setUpUserVoiceAnimation() {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    const microphone = audioContext.createMediaStreamSource(stream);
    microphone.connect(analyser);

    // updateUserVoiceIndicator
    const userVolumeScaleDataArray = new Uint8Array(analyser.frequencyBinCount);
    const updateUserVoiceIndicator = () => {
      analyser.getByteTimeDomainData(userVolumeScaleDataArray);
      const volume = Math.max(...userVolumeScaleDataArray); // Simplified volume calculation

      setUserVoiceIndicator(volume + 50); // Update size based on volume
      const grayValue = Math.min(240, volume * 2);
      setUserVoiceIndicatorBgColor(
        `rgb(${grayValue}, ${grayValue}, ${grayValue})`
      );

      requestAnimationFrame(updateUserVoiceIndicator);
    };
    updateUserVoiceIndicator();
  }

  async function setUpRecorder() {
    try {
      await wavRecorderRef.current.begin();

      await voiceRecordStart();

      console.log("setup listener");
      await setUpUserVoiceAnimation();
    } catch (err) {
      console.error("Could not start audio recording:", err);
    }
  }

  async function voiceRecordStart() {
    try {
      if (wavRecorderRef.current) {
        await wavRecorderRef.current.record((data) => {
          if (webSocket.current && webSocket.current.readyState === 1) {
            webSocket.current.send(data.mono);
          }
        });
        setState(listening_state);
      } else {
        throw Error("wavRecorderRef not found");
      }
    } catch (err) {
      console.error("voiceRecordStart failed", err);
    }
  }

  function voiceRecordResume() {
    setState(listening_state);
  }

  function voiceRecordPause() {
    setState(pause_state);
  }

  async function voiceRecordStop() {
    try {
      if (wavRecorderRef.current) {
        await wavRecorderRef.current.end();
      }
    } catch (err) {
      console.error("voiceRecordStop failed", err);
    }
  }

  function onBotReplyAudioEnded() {
    console.log("onBotReplyAudioEnded");
    setState(listening_state);
  }

  useEffect(() => {
    initConnection();

    return () => {
      voiceRecordStop();
    };
  }, []);

  // trigger mock listening state
  useEffect(() => {
    if (!testUserSpeaking) {
      setTextReplayMessage(null);
      setInitialBotReplyReceived(false);
      setState(init_state);
      return;
    }

    const updateVolumn = () => {
      const volume = Math.floor(Math.random() * (100 - 20 + 1)) + 20;
      setUserVoiceIndicator(volume);
      // mock state
      setTextReplayMessage("Welcome");
      setInitialBotReplyReceived(true);
      setState(listening_state);
    };

    const intervalId = setInterval(updateVolumn, 100);

    return () => clearInterval(intervalId);
  }, [testUserSpeaking]);

  const onHandleAudioController = event => {
    if (event === 'end') {
      toggleVoiceMode()
    }
  }

  return (
    <>
      {/*<p className="text-gray-50 bg-blue-300">{state}</p>*/}
      <div style={{ height: "100dvh" }}
           className="h-screen bg-gray-950 bg-opacity-100 lg:bg-opacity-95"
      >
        {/*{voiceReplayMessage && (*/}
        {/*    <audio*/}
        {/*        autoPlay={true}*/}
        {/*        src={voiceReplayMessage}*/}
        {/*        onEnded={onBotReplyAudioEnded}*/}
        {/*    />*/}
        {/*)}*/}

        {/*{voiceReplayBytes && <AudioPlayer audioData={voiceReplayBytes} reset={state === thinking_state} />}*/}

        <div className="plugin-chat-voice-wrapper flex h-full flex-1 flex-col justify-between">
          {/* Voice header  */}
          <VoiceHeader
            settingsState={settingsState}
            chatBotSetting={chatBotSetting}
            toggleVoiceMode={toggleVoiceMode} // Pass toggle function to VoiceHeader
          />

          {/* Voice view  */}
          <div className="flex-1 bg-inherit">
            <div className="h-full flex flex-col justify-center items-center">
              {/* {textReplayMessage && (
                <DataDisplayFactory
                  message={textReplayMessage}
                  onInvokeAction={onInvokeAction}
                />
              )} */}
              {/* Voice state animation */}
              <div className="p-14 flex flex-col space-y-8 items-center justify-center">
                {(state === init_state || state === thinking_state) && (
                  <AnalysingIndicator />
                )}

                {(state !== init_state) && (state === listening_state) && (
                  // <VoiceIndicator
                  //   size={userVoiceIndicatorSize}
                  //   bgColor={userVoiceIndicatorBgColor}
                  // />
                    <ReadingIndicator
                        size={userVoiceIndicatorSize}
                        bgColor={userVoiceIndicatorBgColor}
                    />
                )}

                {(state !== init_state) && (state === reading_answer_state) && (
                    <ReadingIndicator
                        size={userVoiceIndicatorSize}
                        bgColor={userVoiceIndicatorBgColor}
                    />
                )}

                {/*{state === thinking_state && (*/}
                {/*  <AnalysingToast text="Processing..." />*/}
                {/*)}*/}

                {initialBotReplyReceived && state === listening_state && (
                  <AnalysingToast text=" I'm Listening..." />
                )}
              </div>
            </div>
          </div>

          {/* Voice control tools & options */}
          <div className="px-5 lg:px-0 bg-gray-950 lg:border-t border-gray-800 pt-2">
            {/* Voice chat control options */}
            <AudioControlTool controlClick={onHandleAudioController}
            />
          </div>
        </div>
      </div>
    </>
  );
}

const mapsStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    voiceModeChangeHandle: (status) => {
      dispatch(voiceModeChange(status));
    },
  };
};

VoiceModePlugin.propTypes = {
  accessKey: PropTypes.string.isRequired,
  appId: PropTypes.string.isRequired,
  sessionId: PropTypes.string.isRequired,
  settingsState: PropTypes.any,
  chatBotSetting: PropTypes.any,
  toggleVoiceMode: PropTypes.func.isRequired, // Add prop type validation
  onInvokeAction: PropTypes.func,
};

export default connect(mapsStateToProps, mapDispatchToProps)(VoiceModePlugin);
